/**
 * Pre-login Theresias event buffer (localStorage-backed FIFO queue).
 *
 * Why: `useIntentEventFirer` (postIntentEvent.ts) silently no-ops when
 * `accessJWT` or `userInfo.id` is missing — which is the entire onboarding
 * funnel (home → travelWith → lookingFor → budget → singUp). Pre-signup
 * events for 22 Theresias intents (budget/travel/vibe/home-city + their
 * `-next-click` siblings) were therefore dropped on the floor.
 *
 * Strategy: when the auth gate trips, callers enqueue the event here. After
 * login completes (StateContext populates both fields), `useFlushPendingTheresias`
 * drains this queue and re-fires each entry through the normal transport with
 * the now-known userId + JWT and the original `context.ts` timestamp preserved.
 *
 * Storage characteristics:
 *   - Key: `_zenvoya_pending_theresias_v1` (cleared by `logoutClearStorage`).
 *   - Cap: 50 entries (FIFO eviction — drop oldest on overflow).
 *   - TTL: 7 days at drain time (onboarding commonly spans multiple sessions;
 *     older than that = noise).
 *   - `event_uuid`: per-entry random id so server-side dedup can collapse the
 *     rare double-drain case (multi-tab race: Tab A logs in and drains
 *     localStorage while Tab B's flush hook hasn't yet noticed).
 */

const STORAGE_KEY = "_zenvoya_pending_theresias_v1";
const QUEUE_CAP = 50;
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface PendingTheresiasEntry {
  event_uuid: string;
  event_name: string;
  custom?: Record<string, unknown>;
  context?: Record<string, unknown>;
  queued_at: number;
}

// `crypto.randomUUID` is the modern API but missing in pre-2021 browsers and
// non-secure contexts. Fall back to a timestamp + random string of comparable
// uniqueness — collision risk is negligible at the 50-entry-cap scale we run
// at. Matches the same fallback shape as `App.tsx`'s `generateTabId`.
function generateEventUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `pe-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readQueue(): PendingTheresiasEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is PendingTheresiasEntry =>
        e &&
        typeof e.event_uuid === "string" &&
        typeof e.event_name === "string" &&
        typeof e.queued_at === "number"
    );
  } catch {
    // Corrupt or unreadable queue — treat as empty. Don't propagate.
    return [];
  }
}

function writeQueue(entries: PendingTheresiasEntry[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // QuotaExceeded / private-mode / disabled storage. Pre-login telemetry
    // is best-effort; never surface the error.
  }
}

/**
 * Append a new entry to the pending queue. Generates `event_uuid` if the
 * caller didn't supply one. Cap 50 — older entries evict FIFO.
 */
export function pushPendingEvent(
  partial: Omit<PendingTheresiasEntry, "event_uuid" | "queued_at"> &
    Partial<Pick<PendingTheresiasEntry, "event_uuid" | "queued_at">>
): void {
  const entry: PendingTheresiasEntry = {
    event_uuid: partial.event_uuid ?? generateEventUuid(),
    event_name: partial.event_name,
    custom: partial.custom,
    context: partial.context,
    queued_at: partial.queued_at ?? Date.now(),
  };
  const current = readQueue();
  const next = [...current, entry];
  // FIFO eviction beyond cap — drop the oldest first.
  const trimmed = next.length > QUEUE_CAP ? next.slice(next.length - QUEUE_CAP) : next;
  writeQueue(trimmed);
}

/**
 * Atomic read + clear. Returns all entries within TTL, sorted by queue order
 * (oldest first — preserves the onboarding funnel order budget→travel→vibe
 * when the flush hook replays them sequentially). Entries older than TTL are
 * dropped silently.
 */
export function drainPendingEvents(): PendingTheresiasEntry[] {
  const all = readQueue();
  // Clear FIRST so a re-entrant call (or a parallel tab racing in) doesn't
  // double-drain. The returned array is the only handle to these entries.
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort.
  }
  const cutoff = Date.now() - TTL_MS;
  return all.filter((e) => e.queued_at >= cutoff);
}

/**
 * Wipe the queue. Called from `logoutClearStorage` via the shared keysToRemove
 * list — kept as a named export so tests / future callers don't have to know
 * the storage key.
 */
export function clearPendingEvents(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort.
  }
}

/**
 * Read-only — for diagnostic UIs or tests. Does NOT clear.
 */
export function peekPendingEvents(): PendingTheresiasEntry[] {
  return readQueue();
}
